import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, PlayCircle, Scale } from "lucide-react";
import { toast } from "sonner";
import { startFoundationARReplay, getFoundationARReplayRun, getFoundationARReplayRuns, reconcileFoundationAR } from "@/lib/api";

const OUT_CLS = {
  success: "text-emerald-700", already_in_foundation: "text-emerald-700", blocked: "text-amber-800",
  skipped: "text-slate-500", sql_error: "text-rose-700", bridge_error: "text-rose-700", failed: "text-rose-700", duplicate: "text-slate-500",
};
const REC_CLS = { matched: "text-emerald-700", mismatch: "text-rose-700", missing: "text-rose-700", unchecked: "text-slate-500" };
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

// Backlog replay: one invoice per transaction, in order; each write is
// preceded by a live "is this invoice_no already in ar_invoice?" check so a
// re-run can never double-post; finishes with a per-invoice reconcile.
export function ARReplayPanel({ total, disabled, disabledReason, onDone }) {
  const [run, setRun] = useState(null);
  const [runs, setRuns] = useState([]);
  const [starting, setStarting] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const timer = useRef(null);

  const loadRuns = useCallback(() => getFoundationARReplayRuns(5).then((r) => setRuns(r.data)).catch(() => {}), []);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const poll = useCallback((id) => {
    clearTimeout(timer.current);
    getFoundationARReplayRun(id).then(({ data }) => {
      setRun(data);
      if (data.status === "running") timer.current = setTimeout(() => poll(id), 1500);
      else { loadRuns(); onDone?.(); }
    }).catch(() => { timer.current = setTimeout(() => poll(id), 3000); });
  }, [loadRuns, onDone]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const start = async () => {
    if (!window.confirm(`Replay ${total} queued invoice(s) into Foundation, one at a time? Each invoice is checked in ar_invoice first so nothing is written twice.`)) return;
    setStarting(true);
    try {
      const { data } = await startFoundationARReplay({ limit: 200 });
      toast.success(`Replay started — ${data.total} invoice(s)`);
      setRun(data);
      poll(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start replay");
    } finally { setStarting(false); }
  };

  const reconcile = async () => {
    setReconBusy(true);
    try {
      const { data } = await reconcileFoundationAR(null);
      setRecon(data);
      toast[data.summary.mismatch || data.summary.missing ? "error" : "success"](`Reconciled ${data.total}: ${data.summary.matched} matched · ${data.summary.mismatch} mismatch · ${data.summary.missing} missing`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally { setReconBusy(false); }
  };

  const c = run?.counts || {};
  return (
    <div className="mb-2 rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="ar-replay-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-slate-600">
          <strong>Backlog replay</strong> — sequential, idempotent (live duplicate check before every write), reconciled per invoice afterwards.
          {disabled && <span className="text-amber-800"> {disabledReason}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={reconcile} disabled={reconBusy}
                  className="h-7 px-2 text-[11px] rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1 disabled:opacity-50"
                  title="Reads every pushed invoice back from ar_invoice / ar_invoice_gl and compares the amounts. Writes nothing."
                  data-testid="ar-reconcile-btn">
            {reconBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scale className="w-3 h-3" />} Reconcile pushed
          </button>
          <button type="button" onClick={start} disabled={disabled || starting || total === 0 || run?.status === "running"}
                  className="h-7 px-2 text-[11px] rounded border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ar-replay-start-btn">
            {starting || run?.status === "running" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} Replay queue ({total})
          </button>
        </div>
      </div>

      {run && (
        <div className="mt-2 text-[11px]" data-testid="ar-replay-run">
          <div className="flex items-center gap-2 flex-wrap text-slate-700">
            <span className={`font-semibold ${run.status === "running" ? "text-sky-700" : "text-slate-800"}`}>{run.status === "running" ? "Running" : "Finished"}</span>
            <span>{run.done}/{run.total}</span>
            <span className="text-emerald-700">pushed {c.pushed || 0}</span>
            <span className="text-emerald-700">already there {c.already_in_foundation || 0}</span>
            <span className="text-amber-800">blocked {c.blocked || 0}</span>
            <span className="text-rose-700">failed {c.failed || 0}</span>
            <span className="text-slate-500">skipped {c.skipped || 0}</span>
            <span className="text-slate-400">→ {run.database}</span>
          </div>
          {run.stopped_reason && <div className="text-rose-700 mt-0.5" data-testid="ar-replay-stopped">Stopped: {run.stopped_reason}</div>}
          <div className="mt-1 max-h-48 overflow-auto rounded border border-slate-200 bg-white">
            {(run.results || []).map((r) => (
              <div key={r.invoice_id} className="px-2 py-1 border-b border-slate-100 last:border-b-0 flex gap-2" data-testid={`ar-replay-result-${r.invoice_id}`}>
                <span className="font-mono w-24 shrink-0">{r.invoice_number}</span>
                <span className={`w-36 shrink-0 font-semibold ${OUT_CLS[r.outcome] || "text-slate-600"}`}>{r.outcome}</span>
                <span className="text-slate-600 break-words min-w-0">{r.plain_error || r.message}</span>
              </div>
            ))}
          </div>
          {run.reconcile?.total > 0 && <ReconcileTable data={run.reconcile} testId="ar-replay-reconcile" />}
        </div>
      )}
      {recon && <ReconcileTable data={recon} testId="ar-reconcile-result" />}
      {!run && runs.length > 0 && (
        <div className="mt-1.5 text-[10px] text-slate-500" data-testid="ar-replay-history">
          Last replay {String(runs[0].started_at).slice(0, 16).replace("T", " ")} by {runs[0].by}: {runs[0].counts?.pushed || 0} pushed · {runs[0].counts?.already_in_foundation || 0} already there · {runs[0].counts?.failed || 0} failed
          <button type="button" className="ml-2 text-blue-600 hover:underline" onClick={() => poll(runs[0].id)} data-testid="ar-replay-show-last">show</button>
        </div>
      )}
    </div>
  );
}

function ReconcileTable({ data, testId }) {
  const s = data.summary || {};
  return (
    <div className="mt-1.5" data-testid={testId}>
      <div className="text-[11px] text-slate-700"><strong>Reconcile</strong> {data.total}: <span className="text-emerald-700">{s.matched || 0} matched</span> · <span className="text-rose-700">{s.mismatch || 0} mismatch</span> · <span className="text-rose-700">{s.missing || 0} missing in Foundation</span>{s.unchecked ? ` · ${s.unchecked} unchecked` : ""}{data.error && <span className="text-rose-700"> — {data.error}</span>}</div>
      <div className="mt-1 max-h-40 overflow-auto rounded border border-slate-200 bg-white text-[10px]">
        {(data.rows || []).map((r) => (
          <div key={r.invoice_id} className="px-2 py-0.5 border-b border-slate-100 last:border-b-0 flex gap-2" data-testid={`ar-reconcile-row-${r.invoice_id}`}>
            <span className="font-mono w-24 shrink-0">{r.invoice_number}</span>
            <span className={`w-16 shrink-0 font-semibold ${REC_CLS[r.result] || ""}`}>{r.result}</span>
            <span className="text-slate-600 min-w-0 break-words">
              app {money(r.expected.invoice_amount)} + tax {money(r.expected.tax_amount)} = {money(r.expected.amount_due)}
              {r.foundation ? ` · Foundation ${money(r.foundation.invoice_amount)} + ${money(r.foundation.tax_amount)} = ${money(r.foundation.amount_due)} · GL ${money(r.foundation.gl_total)} (${r.foundation.gl_lines} line${r.foundation.gl_lines === 1 ? "" : "s"}) · posted ${r.foundation.posted_flag}` : ""}
              {r.diffs?.length ? <span className="text-rose-700"> · {r.diffs.join("; ")}</span> : null}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
